import cs from "classnames";
import { Button, Input, MagellanPopup } from "components/atoms";
import React, {
  CSSProperties,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import Cropper, { Area } from "react-easy-crop";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  PiCheck,
  PiFolder,
  PiImageSquare,
  PiImageSquareLight,
  PiMagnifyingGlassMinus,
  PiMagnifyingGlassPlus,
  PiPencil,
  PiPlus,
  PiTrash,
  PiX,
  PiDownload,
  PiFloppyDisk,
} from "react-icons/pi";
import Popup from "reactjs-popup";
import { PopupActions } from "reactjs-popup/dist/types";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { ILoadingProp } from "types/components/components.interface";
import {
  IImageSize,
  ISelectOption,
} from "types/components/inputs/input.interface";
import { getCroppedImg, getImageSize } from "utils/components/inputs/image";
import "./ImageInput.scss";

export interface IImageInputProps extends ILoadingProp {
  value?: File | null;

  fullResolutionUrl?: string;
  previewUrl?: string;
  alt?: string;
  editable?: boolean;
  title?: string;

  onSave?: (
    croppedFile: File,
    actions: PopupActions,
    clearAllValues?: () => void,
  ) => void;
  onDelete?: (actions: PopupActions) => void;
  onCancel?: (actions: PopupActions) => void;
  onClose?: (
    event?:
      | SyntheticEvent<Element, Event>
      | KeyboardEvent
      | TouchEvent
      | MouseEvent
      | undefined,
  ) => void;

  onChange?: (file: File, actions?: PopupActions) => void;

  disable?: boolean;
  imageSize?: IImageSize;
  imageSizeSelectOptions?: ISelectOption<number>[];
  isError?: boolean;
  imagePlaceholder?: React.ReactNode;
  accept?: string;
}

const ImageInput = ({
  alt,
  editable = false,
  value = null,
  title,
  fullResolutionUrl,
  previewUrl,
  loading,
  onDelete,
  onSave,
  onClose,
  onCancel,
  onChange,
  disable,
  imageSize = getImageSize(500),
  imageSizeSelectOptions,
  isError,
  accept = "image/jpg, image/jpeg",
  imagePlaceholder = <PiImageSquareLight className="placeholder" />,
}: IImageInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<PopupActions>(null);

  const [newImage, setNewImage] = useState<File | null>(null);

  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const [valueUrl, setValueUrl] = useState<string | null>(null);

  const [newImageSize, setNewImageSize] = useState<IImageSize>(imageSize);
  const [imageError, setImageError] = useState(false);

  const zoomSmoothness = 10;
  const maxZoom = 3 * zoomSmoothness;
  const initialCropValues = { x: 0, y: 0 };
  const [crop, setCrop] = useState(initialCropValues);
  const [zoom, setZoom] = useState(zoomSmoothness);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const lastUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const candidate = previewUrl ?? fullResolutionUrl;
    if (candidate) lastUrlRef.current = candidate;
  }, [previewUrl, fullResolutionUrl]);

  const stableUrl = previewUrl ?? fullResolutionUrl ?? lastUrlRef.current;

  const revokeSafe = (url: string | null) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {}
  };

  useEffect(() => {
    if (!value) {
      setValueUrl((prev) => {
        revokeSafe(prev);
        return null;
      });
      return;
    }

    const url = URL.createObjectURL(value);
    setValueUrl((prev) => {
      revokeSafe(prev);
      return url;
    });

    return () => revokeSafe(url);
  }, [value]);

  useEffect(() => {
    if (!newImage) {
      setNewImageUrl((prev) => {
        revokeSafe(prev);
        return null;
      });
      return;
    }

    const url = URL.createObjectURL(newImage);
    setNewImageUrl((prev) => {
      revokeSafe(prev);
      return url;
    });

    return () => revokeSafe(url);
  }, [newImage]);

  useEffect(() => {
    setImageError(false);
  }, [stableUrl, valueUrl, newImageUrl]);

  const clearCrop = () => {
    setCrop(initialCropValues);
    setZoom(zoomSmoothness);
    setCroppedAreaPixels(null);
  };

  const clearNewImageOnly = () => {
    setNewImage(null);
    clearCrop();
  };

  const openFiles = () => inputRef.current?.click();

  const cancel = () => {
    popupRef.current && onCancel && onCancel(popupRef.current);
    popupRef.current?.close();
    clearNewImageOnly();
  };

  const triggerUrl = valueUrl ?? stableUrl;

  const cropperSourceUrl = newImageUrl ?? valueUrl ?? stableUrl;

  const canEditCrop = Boolean(newImageUrl ?? valueUrl);

  const onCropComplete = (_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  };

  const onDownloadImage = () =>
    toast.success(t("toast-message.image-downloaded-successfully"));

  const triggerClassNames = cs(["image-input-trigger"], {
    editable: !disable && editable,
    loading: loading,
    disable: disable,
    error: !disable && isError,
    downloadable: !disable && !editable && triggerUrl,
  });

  const applyCrop = async () => {
    const sourceFile = newImage ?? value;
    if (!sourceFile) return;

    const areaPixels =
      croppedAreaPixels ??
      ({
        x: 0,
        y: 0,
        width: newImageSize.width,
        height: newImageSize.height,
      } as Area);

    const croppedFile = await getCroppedImg(
      sourceFile,
      areaPixels,
      newImageSize.height,
      newImageSize.width,
    );

    onChange?.(croppedFile, popupRef.current ?? undefined);

    if (onSave && popupRef.current) {
      onSave(croppedFile, popupRef.current, clearNewImageOnly);
    }

    clearNewImageOnly();
  };

  const onDeleteClick = () => {
    if (newImage) {
      clearNewImageOnly();
      return;
    }
    if (popupRef.current && onDelete) {
      onDelete(popupRef.current);
    }
  };

  return (
    <Popup
      ref={popupRef}
      contentStyle={
        {
          "--width-multiple": imageSize.width / imageSize.height,
        } as CSSProperties
      }
      trigger={
        <div className={triggerClassNames}>
          {triggerUrl && !imageError ? (
            !editable && (fullResolutionUrl || triggerUrl) ? (
              <a
                href={fullResolutionUrl ?? triggerUrl}
                className="download-href"
                onClick={onDownloadImage}
              >
                <img
                  src={triggerUrl}
                  alt={alt ?? triggerUrl}
                  loading="lazy"
                  onError={() => setImageError(true)}
                />
              </a>
            ) : (
              <img
                src={triggerUrl}
                alt={alt ?? triggerUrl}
                loading="lazy"
                onError={() => setImageError(true)}
              />
            )
          ) : (
            imagePlaceholder
          )}

          <div className="hover-overlay">
            {!disable && editable ? (
              triggerUrl ? (
                <PiPencil />
              ) : (
                <PiPlus className="new" />
              )
            ) : (
              triggerUrl && <PiDownload />
            )}
          </div>
        </div>
      }
      onOpen={() => {
        clearCrop();
        if (!triggerUrl && !newImage) openFiles();
      }}
      disabled={!editable || disable}
      onClose={onClose}
      modal
      nested
    >
      <MagellanPopup className="image-popup">
        <MagellanPopup.Header>
          <PiImageSquare />
          {title ?? t("image-input-popup.header")}
        </MagellanPopup.Header>

        {(newImage || cropperSourceUrl) && imageSizeSelectOptions && (
          <div className="size-select">
            <Input.Select
              label={t("image-input-popup.image-size")}
              value={{
                label: `${newImageSize.height} px`,
                value: newImageSize,
              }}
              options={imageSizeSelectOptions}
              onChange={(option) =>
                setNewImageSize(getImageSize(option.value as number))
              }
            />
          </div>
        )}

        <div
          className={`crop-wrapper${
            imageSize.width === imageSize.height ? " square" : ""
          }`}
        >
          {!cropperSourceUrl ? (
            imagePlaceholder
          ) : (
            <Cropper
              image={cropperSourceUrl}
              crop={crop}
              zoom={zoom / zoomSmoothness}
              aspect={imageSize.width / imageSize.height}
              onCropChange={(location) => canEditCrop && setCrop(location)}
              onCropComplete={onCropComplete}
              onZoomChange={(z) => canEditCrop && setZoom(z * zoomSmoothness)}
              objectFit="cover"
            />
          )}
        </div>

        <div className="zoom-range">
          <PiMagnifyingGlassMinus />
          <input
            type="range"
            value={zoom}
            onChange={(e) => setZoom(parseInt(e.target.value))}
            min={zoomSmoothness}
            max={maxZoom}
            disabled={!canEditCrop}
          />
          <PiMagnifyingGlassPlus />
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="invisible-file-input"
          onChange={(e) =>
            e.target.files?.length && setNewImage(e.target.files[0])
          }
        />

        <div className="buttons">
          <div className="flex gap-2">
            {onDelete && (
              <Button
                variant={BUTTON_VARIANT.remove}
                onlyIcon
                isClear
                onClick={onDeleteClick}
              >
                <Button.Icon icon={<PiTrash />} />
              </Button>
            )}
            <Button onlyIcon isClear onClick={openFiles}>
              <Button.Icon icon={<PiFolder />} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button onlyIcon isClear onClick={cancel}>
              <Button.Icon icon={<PiX />} />
            </Button>

            {onSave ? (
              <Button
                variant={BUTTON_VARIANT.submit}
                disabled={!(newImage || value)}
                onClick={applyCrop}
              >
                <Button.Text text={t("image-input-popup.save")} />
                <Button.Icon icon={<PiFloppyDisk />} />
              </Button>
            ) : (
              <Button
                variant={BUTTON_VARIANT.submit}
                onlyIcon
                disabled={!(newImage || value)}
                onClick={async () => {
                  await applyCrop();
                  popupRef.current?.close();
                }}
              >
                <Button.Icon icon={<PiCheck />} />
              </Button>
            )}
          </div>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default ImageInput;
